import type { SectionId } from "./sections";

// NOTE: These are sample stories that show off the layout. Replace them with
// real reporting (or wire this module to a CMS) before going live.

export interface Article {
  slug: string;
  section: SectionId;
  kicker?: string;
  title: string;
  /** Short all-caps splash headline used when the story leads the page. */
  splash?: string;
  dek: string;
  author: string;
  city?: string;
  /** How long ago the story was published; turned into a real timestamp at runtime. */
  hoursAgo: number;
  image: string;
  imageCaption?: string;
  body: string[];
  tags: string[];
  video?: boolean;
  exclusive?: boolean;
}

const img = (id: string) => `https://images.unsplash.com/photo-${id}?auto=format&fit=crop&w=1400&q=70`;

export const ARTICLES: Article[] = [
  {
    slug: "mumbai-rents-push-young-renters-to-navi-mumbai",
    section: "news",
    kicker: "Rent Shock",
    title: "Mumbai's sky-high rents are driving young renters across the harbour to Navi Mumbai",
    splash: "Rent Refugees",
    dek: "One-bedroom rents in the western suburbs now swallow half a starting salary. So the 25-year-olds are packing up.",
    author: "Ananya Kulkarni",
    city: "Mumbai",
    hoursAgo: 1,
    image: img("1570168007204-dfb528c6958f"),
    imageCaption: "Mumbai's skyline. Rents in the western suburbs have climbed steadily since 2022.",
    exclusive: true,
    tags: ["Mumbai", "Rentals", "Navi Mumbai"],
    body: [
      "For a growing number of young professionals, the commute is the price of a roof. Brokers across Andheri, Bandra and Goregaon say they are seeing fewer first-job tenants sign leases, and more of them heading to Kharghar, Ulwe and Panvel instead.",
      "A BrickBharat review of listings in three western-suburb micro-markets found that a furnished one-bedroom routinely asks more than half of a typical entry-level salary in the city. Across the harbour, a comparable flat can cost a little over half as much.",
      "The new airport and trans-harbour link have changed the maths, brokers say. What used to be a two-hour trip to the island city is now far more predictable, and young renters are willing to trade a little time for a lot of space.",
      "## Landlords feel the shift",
      "Some landlords in the suburbs are already responding. Several brokers told BrickBharat that owners who held out for top-dollar rents in spring are now accepting smaller deposits or throwing in furniture to close deals.",
      "> \"Two years ago I would get ten calls for a one-BHK in Andheri West in a day. Now I get ten calls and six of them ask me what I have in Ulwe.\"",
      "Economists caution that the migration is gradual, and that the island city's rental demand remains deep. But for anyone in their twenties apartment hunting this month, the message from the market is clear: look east.",
    ],
  },
  {
    slug: "delhi-ncr-luxury-launches-sell-out-in-days",
    section: "luxury",
    kicker: "Sold Out",
    title: "Gurugram's ₹10 crore apartments are selling out in days, and nobody is quite sure who is buying",
    splash: "Crore Rush",
    dek: "Developers on the Golf Course Extension say NRIs and founders are snapping up units before the brochures are printed.",
    author: "Rohan Mehra",
    city: "Delhi NCR",
    hoursAgo: 2,
    image: img("1613490493576-7fde63acd811"),
    imageCaption: "Ultra-luxury launches in Gurugram now routinely list above ₹10 crore.",
    tags: ["Gurugram", "Luxury", "Launches"],
    body: [
      "On a Saturday morning in Sector 65, the sales lounge of a new tower looked more like a private club than a site office: valet parking, espresso, and a queue of buyers holding pre-approved cheques.",
      "Luxury launches along Gurugram's Golf Course Extension Road have repeatedly been reported as sold out within days of opening, even as ticket sizes have climbed well past ₹10 crore.",
      "Sales heads at three developers told BrickBharat that buyers break roughly into three camps: non-resident Indians returning with dollar and dirham savings, start-up founders cashing out, and older business families upgrading from bungalows in south Delhi.",
      "## Is it a bubble?",
      "Analysts are split. Some argue that genuine end-users, not speculators, are driving the demand, pointing to low resale volumes in recent luxury projects. Others worry that prices have outrun rents so far that yields now barely beat a savings account.",
      "What nobody disputes is that the top end of the market has decoupled from the rest. In the same city, affordable housing launches have slowed to a trickle.",
    ],
  },
  {
    slug: "rera-orders-builder-to-refund-homebuyers-with-interest",
    section: "policy",
    kicker: "RERA",
    title: "Homebuyers win big as RERA orders stalled project's builder to refund every rupee, with interest",
    dek: "The authority said a delay of more than four years was 'a clear breach' and gave the developer 60 days to pay up.",
    author: "Priya Nair",
    city: "Pune",
    hoursAgo: 3,
    image: img("1541888946425-d81bb19240f5"),
    imageCaption: "An under-construction site. Delayed projects remain the single largest source of RERA complaints.",
    tags: ["RERA", "Refunds", "Homebuyers"],
    body: [
      "A group of more than 40 homebuyers who have waited over four years for possession of their flats have finally won a round, after the state's real estate regulator ordered the developer to refund their payments with interest.",
      "The order, reviewed by BrickBharat, found that the promoter failed to complete the project by the date registered with the authority and did not seek a valid extension.",
      "Under the Real Estate (Regulation and Development) Act, 2016, buyers who choose to exit a delayed project are entitled to a refund of the amount paid along with interest at the rate prescribed by the state rules.",
      "## What buyers in similar projects should do",
      "Lawyers say buyers in delayed projects should first check the completion date registered on their state RERA portal, keep every payment receipt and communication, and file a complaint online. Most authorities now allow hearings by video call.",
      "> \"The law is on the buyer's side. The hard part is enforcement, which is why buyers should file early and follow up relentlessly.\"",
    ],
  },
  {
    slug: "bengaluru-office-leasing-record-quarter",
    section: "commercial",
    kicker: "Office Boom",
    title: "Bengaluru just had another monster quarter for office leasing, and global tech is the reason",
    dek: "Global capability centres now account for the lion's share of new office demand along the Outer Ring Road.",
    author: "Karthik Rao",
    city: "Bengaluru",
    hoursAgo: 4,
    image: img("1497366216548-37526070297c"),
    tags: ["Bengaluru", "Office", "GCC"],
    body: [
      "Bengaluru has once again topped India's office leasing charts, with brokers reporting that global capability centres, the in-house tech and operations hubs of multinational companies, signed the biggest deals of the quarter.",
      "The Outer Ring Road and the northern corridor towards the airport continue to dominate, but brokers say Whitefield and Sarjapur Road are catching up as landlords deliver new Grade-A towers.",
      "Rents for premium space have edged up, and vacancy in the best buildings is tight enough that some occupiers are pre-committing to towers that are still under construction.",
      "For investors, the boom is also fuelling India's listed REITs, whose portfolios are heavily weighted towards Bengaluru offices.",
    ],
  },
  {
    slug: "navi-mumbai-airport-land-prices",
    section: "infrastructure",
    kicker: "Takeoff",
    title: "The Navi Mumbai airport effect: land prices around Panvel and Ulwe keep climbing",
    dek: "Plots that went begging a decade ago are now being fought over by developers and warehousing firms alike.",
    author: "Ananya Kulkarni",
    city: "Mumbai",
    hoursAgo: 5,
    image: img("1436491865332-7a61a109cc05"),
    tags: ["Navi Mumbai", "Airport", "Land"],
    body: [
      "Few infrastructure projects have moved property prices quite like Navi Mumbai's international airport. Brokers in Ulwe, Panvel and Dronagiri say land that barely traded a decade ago is now the subject of bidding wars.",
      "Residential developers are racing to launch projects within a short drive of the terminal, while logistics firms are buying land further out for warehouses that can serve both the airport and the JNPA port.",
      "Planners caution that buyers should check the zoning and height restrictions that apply close to an airport before paying a premium for a plot.",
    ],
  },
  {
    slug: "home-loan-emi-calculator-what-rate-cut-means",
    section: "finance",
    kicker: "Your Money",
    title: "What a quarter-point rate cut actually does to your home loan EMI",
    dek: "We ran the numbers on a ₹50 lakh, 20-year loan. The answer depends on one question your bank may not ask you.",
    author: "Neha Sharma",
    hoursAgo: 6,
    image: img("1554224155-6726b3ff858f"),
    tags: ["Home Loans", "EMI", "Interest Rates"],
    body: [
      "Every time the Reserve Bank moves its policy rate, borrowers ask the same question: how much will my EMI change? For most floating-rate home loans linked to an external benchmark, the change flows through at the next reset date.",
      "On a ₹50 lakh loan over 20 years, a cut of 0.25 percentage points trims the EMI by several hundred rupees a month. That sounds small, but over the life of the loan it adds up to well over a lakh in interest.",
      "## Tenure or EMI?",
      "Here is the question many banks will not ask: when rates fall, would you rather pay a lower EMI, or keep the EMI the same and finish the loan sooner? Keeping the EMI unchanged and letting the tenure shrink usually saves far more interest.",
      "Borrowers on older MCLR or base-rate loans should check whether switching to an external-benchmark loan makes sense. A small conversion fee can pay for itself quickly.",
    ],
  },
  {
    slug: "hyderabad-housing-sales-west-corridor",
    section: "residential",
    kicker: "Hot Market",
    title: "Hyderabad's western corridor is where the city's home buyers are placing their bets",
    dek: "Kokapet, Narsingi and Tellapur are drawing IT families who want bigger homes near the Financial District.",
    author: "Srinivas Reddy",
    city: "Hyderabad",
    hoursAgo: 7,
    image: img("1545324418-cc1a3fa10c00"),
    tags: ["Hyderabad", "Housing Sales"],
    body: [
      "The high-rises of Kokapet have become the new face of Hyderabad real estate. Developers say demand for large three- and four-bedroom flats close to the Financial District has held up even as sales slowed elsewhere.",
      "Buyers are largely salaried technology professionals upgrading from rented flats in Gachibowli and Madhapur, brokers say.",
      "Supply is the worry. A wave of launches means buyers have a lot of choice, and several analysts expect discounts on slower-moving projects.",
    ],
  },
  {
    slug: "pune-affordable-housing-squeeze",
    section: "residential",
    kicker: "Squeezed Out",
    title: "Where did all the affordable flats go? Pune buyers under ₹50 lakh are running out of options",
    dek: "Rising land and construction costs have pushed developers upmarket, leaving first-time buyers stranded.",
    author: "Priya Nair",
    city: "Pune",
    hoursAgo: 9,
    image: img("1460317442991-0ec209397118"),
    tags: ["Pune", "Affordable Housing"],
    body: [
      "For years, Pune was the city where a young couple could buy a two-bedroom flat without a crushing loan. That is getting harder.",
      "Developers say rising land prices and construction costs make it difficult to build profitably at lower price points, and new launches have shifted towards larger, pricier homes.",
      "First-time buyers are responding by moving further out along the Hinjewadi and Wagholi corridors, or by opting for resale flats in older societies.",
    ],
  },
  {
    slug: "chennai-omr-plot-demand",
    section: "cities",
    kicker: "Chennai",
    title: "OMR plots are back in fashion as Chennai families chase independent homes",
    dek: "Gated plotted developments along Old Mahabalipuram Road are drawing buyers who want to build their own house.",
    author: "Lakshmi Iyer",
    city: "Chennai",
    hoursAgo: 11,
    image: img("1564013799919-ab600027ffc6"),
    tags: ["Chennai", "Plots", "OMR"],
    body: [
      "Plotted developments are having a moment in Chennai. Brokers along Old Mahabalipuram Road say families are increasingly choosing to buy a plot and build, rather than buy a flat.",
      "Buyers cite privacy, flexibility and the ability to build in phases. Developers have responded with gated layouts that offer roads, drainage and clubhouses.",
      "Lawyers advise buyers to verify DTCP or CMDA approvals and check the title chain carefully before paying.",
    ],
  },
  {
    slug: "warehousing-demand-tier-2-cities",
    section: "commercial",
    kicker: "Logistics",
    title: "The warehouse gold rush is moving to India's smaller cities",
    dek: "E-commerce and quick-commerce firms want to be within hours of customers in Lucknow, Indore and Coimbatore.",
    author: "Karthik Rao",
    hoursAgo: 13,
    image: img("1586528116311-ad8dd3c8310d"),
    tags: ["Warehousing", "Logistics", "Tier 2"],
    body: [
      "India's warehousing boom has long been concentrated around the big metros. That is changing as online retailers push deeper into smaller cities.",
      "Industrial and logistics parks are coming up on the outskirts of cities such as Lucknow, Indore, Coimbatore and Guwahati, often on land along new highways.",
      "Institutional investors, including global funds, have backed several of the larger platforms.",
    ],
  },
  {
    slug: "stamp-duty-women-buyers-concession",
    section: "policy",
    kicker: "Stamp Duty",
    title: "Buying a home in your wife's or mother's name? Here's how much stamp duty you could save",
    dek: "Several states offer lower stamp duty for women buyers. The savings can run into lakhs on a big-ticket home.",
    author: "Neha Sharma",
    hoursAgo: 15,
    image: img("1560518883-ce09059eeffa"),
    tags: ["Stamp Duty", "Women Buyers", "Taxes"],
    body: [
      "A number of Indian states charge a lower stamp duty when a property is registered in a woman's name, either as sole owner or joint owner.",
      "The concession varies by state and sometimes by city, so buyers should check the current rates with the local sub-registrar's office before registering.",
      "Tax advisers note that the source of funds matters for income-tax purposes, and that the woman buyer should ideally also be a co-borrower on the home loan.",
    ],
  },
  {
    slug: "mumbai-coastal-road-worli-prices",
    section: "infrastructure",
    kicker: "Coastal Road",
    title: "Mumbai's coastal road cut the commute. Now it's pushing up Worli and Marine Drive prices",
    dek: "Sea-facing homes along the route command a premium as drive times to the western suburbs shrink.",
    author: "Ananya Kulkarni",
    city: "Mumbai",
    hoursAgo: 18,
    image: img("1567157577867-05ccb1388e66"),
    tags: ["Mumbai", "Coastal Road", "Worli"],
    body: [
      "The coastal road has done what few Mumbai projects manage: it has visibly shortened a commute. Brokers in Worli, Prabhadevi and Breach Candy say buyers are now pricing that in.",
      "Sea-facing flats with easy access to the road's interchanges are commanding a premium over otherwise similar homes a few streets inland.",
      "Urban planners caution that the full benefit depends on the northern extension being completed on time.",
    ],
  },
  {
    slug: "bengaluru-villa-plots-north",
    section: "luxury",
    kicker: "Villa Life",
    title: "Inside Bengaluru's villa belt, where founders are building homes with private forests",
    dek: "North Bengaluru near the airport has become a magnet for tech money looking for space and quiet.",
    author: "Karthik Rao",
    city: "Bengaluru",
    hoursAgo: 20,
    image: img("1600596542815-ffad4c1539a9"),
    tags: ["Bengaluru", "Villas", "Luxury"],
    body: [
      "Drive past the airport on the Ballari Road and the landscape changes: gated villa communities with landscaped forests, private pools and clubhouses that would not look out of place in a resort.",
      "Buyers are mostly technology founders and senior executives who have outgrown apartments in Indiranagar or Koramangala.",
      "Developers are marketing sustainability as heavily as luxury, with rainwater harvesting, solar power and native planting now standard selling points.",
    ],
  },
  {
    slug: "opinion-india-needs-rental-housing",
    section: "opinion",
    kicker: "Opinion",
    title: "India's housing crisis isn't about ownership. It's that nobody builds homes to rent",
    dek: "Until institutional rental housing takes off, migrants and young workers will stay stuck in informal, insecure tenancies.",
    author: "Vikram Desai",
    hoursAgo: 22,
    image: img("1502672260266-1c1ef2d93688"),
    tags: ["Opinion", "Rental Housing"],
    body: [
      "Indian housing policy has spent decades obsessed with ownership. Subsidies, tax breaks and schemes all nudge families towards buying. But for the millions who move to cities for work each year, buying is years away, if it ever happens.",
      "What they need is decent, secure rental housing. And almost nobody builds it.",
      "The model rental law was meant to make renting predictable for both landlords and tenants. States that adopt it, and courts that enforce it quickly, could unlock purpose-built rental housing at scale.",
      "> Until renting a home in India feels as safe as renting a car, landlords will keep flats locked up and tenants will keep paying through the nose.",
      "Institutional investors are ready. Co-living operators have shown there is demand. What is missing is policy that treats renting as a first-class way to live, not a waiting room for ownership.",
    ],
  },
  {
    slug: "opinion-first-time-buyers-dont-stretch",
    section: "opinion",
    kicker: "Opinion",
    title: "First-time buyers: stop stretching your budget for a bigger flat",
    dek: "The extra bedroom you'll 'grow into' could cost you a decade of financial freedom.",
    author: "Meera Joshi",
    hoursAgo: 30,
    image: img("1560448204-e02f11c3d0e2"),
    tags: ["Opinion", "Home Buying"],
    body: [
      "Every broker has a favourite line: 'Take the three-bedroom, you'll grow into it.' It is advice that has trapped a generation of buyers in EMIs that eat half their salary.",
      "A sensible rule of thumb is to keep the EMI to a manageable share of take-home pay and keep an emergency fund of six months of expenses before you buy.",
      "You can always upgrade. It is much harder to downgrade once your savings are locked in bricks.",
    ],
  },
  {
    slug: "noida-stalled-projects-revival",
    section: "news",
    kicker: "Stalled Homes",
    title: "Thousands of stuck Noida homebuyers may finally get keys as stalled projects restart",
    dek: "A revival push is bringing cranes back to towers that have stood half-built for years.",
    author: "Rohan Mehra",
    city: "Delhi NCR",
    hoursAgo: 8,
    image: img("1504307651254-35680f356dfd"),
    tags: ["Noida", "Stalled Projects", "Homebuyers"],
    body: [
      "For thousands of families in Noida and Greater Noida, the half-finished towers along the expressway have been a daily reminder of money paid and homes never delivered.",
      "A revival push involving authorities, lenders and co-developers is bringing work back to several of these sites.",
      "Homebuyer associations welcomed the progress but said they would believe it when they have keys in hand.",
    ],
  },
  {
    slug: "reits-small-investors-guide",
    section: "finance",
    kicker: "Invest",
    title: "Want to own a slice of a Bengaluru office tower? A beginner's guide to REITs",
    dek: "Real estate investment trusts let you invest in rent-earning property with the price of a few units on the stock exchange.",
    author: "Neha Sharma",
    hoursAgo: 26,
    image: img("1579621970563-ebec7560ff3e"),
    tags: ["REITs", "Investing"],
    body: [
      "Real estate investment trusts (REITs) own portfolios of rent-earning property, mostly office parks and malls, and are required to distribute most of their income to unitholders.",
      "Units trade on the stock exchange like shares, so investors can get exposure to commercial real estate without buying an entire office floor.",
      "Distributions can be made up of dividend, interest and repayment of capital, each taxed differently, so investors should read the fine print.",
    ],
  },
  {
    slug: "kolkata-new-town-demand",
    section: "cities",
    kicker: "Kolkata",
    title: "New Town and Rajarhat are quietly becoming Kolkata's most wanted address",
    dek: "Wide roads, IT offices and metro connectivity are pulling buyers away from the congested south.",
    author: "Arijit Sen",
    city: "Kolkata",
    hoursAgo: 28,
    image: img("1480074568708-e7b720bb3f09"),
    tags: ["Kolkata", "New Town"],
    body: [
      "Kolkata's property market has long been dominated by the south of the city. That is changing as New Town and Rajarhat mature.",
      "Buyers cite wide roads, planned infrastructure and proximity to the airport and IT hubs.",
      "Prices remain well below those in the older neighbourhoods, which brokers say is a big part of the appeal.",
    ],
  },
  {
    slug: "ahmedabad-gift-city-offices",
    section: "commercial",
    kicker: "GIFT City",
    title: "GIFT City's office towers are filling up as global banks set up shop",
    dek: "India's international financial services centre near Gandhinagar is finally seeing the occupancy it was built for.",
    author: "Harsh Patel",
    city: "Ahmedabad",
    hoursAgo: 33,
    image: img("1486406146926-c627a92ad1ab"),
    tags: ["GIFT City", "Offices", "Ahmedabad"],
    body: [
      "Once dismissed as a ghost town of glass towers, GIFT City is getting busier. Banks, fund managers and fintech firms have been setting up operations in the zone.",
      "Developers say demand has spilled over into housing in Gandhinagar and the Ahmedabad-Gandhinagar corridor.",
      "The next test is whether the city can build the schools, hospitals and social life that will make professionals want to live there, not just work there.",
    ],
  },
  {
    slug: "metro-line-property-premium",
    section: "infrastructure",
    kicker: "Metro Effect",
    title: "How much more should you pay for a flat near a metro station? Brokers weigh in",
    dek: "Walking distance to a station can add a meaningful premium, but only if the line actually opens.",
    author: "Srinivas Reddy",
    hoursAgo: 36,
    image: img("1474487548417-781cb71495f3"),
    tags: ["Metro", "Infrastructure", "Home Buying"],
    body: [
      "Metro connectivity is one of the most reliable drivers of property prices in Indian cities, brokers say. But the premium is not automatic.",
      "Homes within walking distance of an operational station tend to command more than those that depend on a feeder bus or auto-rickshaw.",
      "Buyers paying for a future line should check the project's status and funding, not just the map in the brochure.",
    ],
  },
  {
    slug: "goa-holiday-homes-demand",
    section: "luxury",
    kicker: "Second Homes",
    title: "Goa's villa market is red hot as city buyers chase the work-from-beach dream",
    dek: "North Goa villas with pools are being bought as holiday homes that pay for themselves as rentals.",
    author: "Meera Joshi",
    hoursAgo: 40,
    image: img("1512917774080-9991f1c4c750"),
    tags: ["Goa", "Second Homes", "Villas"],
    body: [
      "Goa has become the second-home capital of India. Buyers from Mumbai, Delhi and Bengaluru are snapping up villas in Assagao, Siolim and Anjuna.",
      "Many plan to rent them out through holiday-rental platforms when they are not visiting.",
      "Lawyers warn buyers to check land-use conversion, coastal regulation zone rules and title carefully; Goa's land records can be complicated.",
    ],
  },
  {
    slug: "pmay-urban-new-phase",
    section: "policy",
    kicker: "Housing For All",
    title: "The government's affordable housing push, explained: who qualifies and how to apply",
    dek: "Income limits, carpet-area caps and paperwork: a plain-English guide for families looking for help buying their first home.",
    author: "Priya Nair",
    hoursAgo: 44,
    image: img("1582407947304-fd86f028f716"),
    tags: ["PMAY", "Affordable Housing", "Policy"],
    body: [
      "India's flagship affordable housing scheme for cities aims to help economically weaker and lower-income families own a home.",
      "Eligibility depends on household income, and applicants generally must not already own a pucca house anywhere in India.",
      "Applications are made through the official portal or designated centres. Beware of agents charging fees to 'guarantee' approval; the scheme does not require them.",
    ],
  },
  {
    slug: "video-tour-worli-penthouse",
    section: "luxury",
    kicker: "Watch",
    title: "Tour a sea-facing Worli penthouse with a private plunge pool 50 floors up",
    dek: "Floor-to-ceiling glass, a wraparound deck and a view all the way to the Bandra-Worli Sea Link.",
    author: "BrickBharat Video",
    city: "Mumbai",
    hoursAgo: 10,
    image: img("1600607687939-ce8a6c25118c"),
    video: true,
    tags: ["Video", "Mumbai", "Penthouse"],
    body: [
      "Our video team got a look inside one of the most spectacular homes on Mumbai's skyline.",
      "The duplex penthouse features a private plunge pool, a home theatre and a wraparound deck overlooking the Arabian Sea.",
    ],
  },
  {
    slug: "video-first-home-checklist",
    section: "finance",
    kicker: "Watch",
    title: "Buying your first flat? The 7-point checklist every buyer should run before paying the token",
    dek: "From RERA registration to the OC, here's what to verify before you sign anything.",
    author: "BrickBharat Video",
    hoursAgo: 16,
    image: img("1600585154340-be6161a56a0c"),
    video: true,
    tags: ["Video", "Home Buying"],
    body: [
      "Check the project's RERA registration and completion date. Verify the title and approvals. Ask for the occupancy certificate on ready homes.",
      "Compare the carpet area in the agreement with the brochure, and never pay a large token amount before seeing the draft agreement.",
    ],
  },
  {
    slug: "video-hyderabad-skyline",
    section: "cities",
    kicker: "Watch",
    title: "From the air: how Hyderabad's skyline changed in ten years",
    dek: "Drone footage of Kokapet, the Financial District and HITEC City.",
    author: "BrickBharat Video",
    city: "Hyderabad",
    hoursAgo: 23,
    image: img("1449844908441-8829872d2607"),
    video: true,
    tags: ["Video", "Hyderabad"],
    body: ["Our drone team flew over Hyderabad's western growth corridor to show how quickly the city's skyline has risen."],
  },
  {
    slug: "video-construction-quality-tips",
    section: "residential",
    kicker: "Watch",
    title: "How to spot poor construction quality in a sample flat, in under 10 minutes",
    dek: "A civil engineer shows the tell-tale signs of cracks, dampness and shortcuts.",
    author: "BrickBharat Video",
    hoursAgo: 38,
    image: img("1503387762-592deb58ef4e"),
    video: true,
    tags: ["Video", "Construction"],
    body: ["A civil engineer walks through a sample flat and points out the details most buyers miss."],
  },
  {
    slug: "co-living-boom-students",
    section: "residential",
    kicker: "Co-Living",
    title: "Co-living operators are racing to house India's students and young workers",
    dek: "Furnished rooms with Wi-Fi, housekeeping and no broker: the model is spreading beyond Bengaluru.",
    author: "Karthik Rao",
    hoursAgo: 48,
    image: img("1522708323590-d24dbb6b0267"),
    tags: ["Co-Living", "Rentals"],
    body: [
      "Co-living, once a Bengaluru niche, is spreading to Pune, Hyderabad, Gurugram and education hubs such as Kota.",
      "Operators lease or buy entire buildings and rent them out by the bed or room, with furniture, Wi-Fi and cleaning included.",
      "Tenants say the appeal is simple: no broker fees, a small deposit and a move-in that takes days rather than weeks.",
    ],
  },
  {
    slug: "data-centres-land-mumbai-chennai",
    section: "commercial",
    kicker: "Data Centres",
    title: "Data centres are the hottest new tenants in Indian real estate",
    dek: "AI and cloud demand have operators hunting for land and power in Navi Mumbai and Chennai.",
    author: "Karthik Rao",
    hoursAgo: 52,
    image: img("1558494949-ef010cbdcc31"),
    tags: ["Data Centres", "Commercial"],
    body: [
      "Data centre operators are among the most aggressive buyers of industrial land in India, with Navi Mumbai and Chennai leading the way thanks to undersea cable landing stations.",
      "Power availability, not just land, is the deciding factor for most sites.",
    ],
  },
];

export const articleBySlug = (slug: string) => ARTICLES.find(a => a.slug === slug);

export const articlesInSection = (section: string) => ARTICLES.filter(a => a.section === section);

export const latest = () => [...ARTICLES].sort((a, b) => a.hoursAgo - b.hoursAgo);

export const TRENDING_SLUGS = [
  "mumbai-rents-push-young-renters-to-navi-mumbai",
  "delhi-ncr-luxury-launches-sell-out-in-days",
  "home-loan-emi-calculator-what-rate-cut-means",
  "rera-orders-builder-to-refund-homebuyers-with-interest",
  "noida-stalled-projects-revival",
  "stamp-duty-women-buyers-concession",
];

export const BREAKING = [
  "RERA orders refund with interest for 40+ buyers in delayed project",
  "Bengaluru tops office leasing charts again as global tech expands",
  "Gurugram ₹10 crore launches sell out within days",
  "Noida stalled towers see cranes return",
  "Navi Mumbai airport corridor land prices keep climbing",
];

export function searchArticles(q: string) {
  const needle = q.trim().toLowerCase();
  if (!needle) return [];
  return latest().filter(a =>
    [a.title, a.dek, a.city ?? "", a.author, ...a.tags].some(f => f.toLowerCase().includes(needle)),
  );
}
